import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { fetchInvoiceBlob } from '../services/subscriptionService';

export interface ViewInvoiceModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should close */
  onClose: () => void;
  /** Zoho invoice id (used for API call) */
  invoiceId: string;
  /** Display label for the invoice (e.g. INV-000560) */
  invoiceNumber: string;
  /** Platform name for the download API query param */
  platformName: string;
}

/**
 * Modal that fetches and displays an invoice (e.g. PDF) via the Zoho
 * invoice download API. Uses the same GET endpoint as download; content
 * is shown in an embed/iframe.
 */
const ViewInvoiceModal: React.FC<ViewInvoiceModalProps> = ({
  open,
  onClose,
  invoiceId,
  invoiceNumber,
  platformName,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch invoice blob when modal opens and we have invoiceId
  useEffect(() => {
    if (!open || !invoiceId) {
      setBlobUrl(null);
      setError(null);
      return;
    }
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    setBlobUrl(null);

    fetchInvoiceBlob(invoiceId, platformName)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load invoice');
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, invoiceId, platformName]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography component="span" variant="h6" sx={{ fontWeight: 'bold' }}>
          Invoice - {invoiceNumber}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'text.secondary' }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, minHeight: 400 }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && blobUrl && (
          <Box
            component="iframe"
            src={blobUrl}
            title={`Invoice ${invoiceNumber}`}
            sx={{
              width: '100%',
              minHeight: 500,
              border: 'none',
              borderRadius: 1,
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewInvoiceModal;
